import React, { useState } from "react";
import { Link } from "react-router-dom";
import { PublicNav } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { KeyRound, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendLink = async () => {
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      setCooldown(60);
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    await sendLink();
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav />
      <div className="grid place-items-center px-6 py-16">
        <div className="w-full max-w-md bg-cream-card border border-cream-deep rounded-3xl p-8 md:p-10">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <MailCheck className="h-14 w-14 text-coral mx-auto" />
              <h1 className="text-2xl font-bold text-brand mt-4">Check your inbox</h1>
              <p className="text-muted-foreground mt-2">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a link
                to reset your password. The link expires in 1 hour.
              </p>
              <p className="text-sm text-muted-foreground mt-4">Didn't get it? Check your spam folder or resend below.</p>
              <Button
                data-testid="forgot-password-resend"
                onClick={sendLink}
                disabled={busy || cooldown > 0}
                variant="outline"
                className="mt-4 rounded-full h-11 px-8 border-cream-deep"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : busy ? "Sending…" : "Resend email"}
              </Button>
              <div>
                <Link to="/login">
                  <Button className="mt-4 rounded-full bg-brand text-white h-11 px-8">Back to login</Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <KeyRound className="h-10 w-10 text-coral" />
              <h1 className="text-2xl font-bold text-brand mt-4">Forgot your password?</h1>
              <p className="text-muted-foreground mt-2">
                Enter the email you use for KOHC and we'll send you a secure link to set a new password.
              </p>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div>
                  <Label>Work email</Label>
                  <Input
                    data-testid="forgot-password-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <Button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full h-11 rounded-full bg-coral hover:bg-coral-hover text-white"
                >
                  {busy ? "Sending…" : "Send reset link"}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-6 text-center">
                Remembered it?{" "}
                <Link to="/login" className="text-brand font-semibold hover:underline">
                  Log in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
